import { handleFetchError } from "../api/request";
import configuration from "../configuration/Configuration"
import { AttributeType, ChangePasswordCommand, CognitoIdentityProviderClient, UpdateUserAttributesCommand } from "@aws-sdk/client-cognito-identity-provider";
import { User } from "oidc-client-ts";
import { Cache } from "../api/cache";

const cognitoIdentityProviderClient = new CognitoIdentityProviderClient({ region: configuration.region })

const cache = new Cache();

export type LoginInfo = Required<Pick<User, 'id_token' | 'refresh_token' | 'access_token'>> & { profile: Pick<User["profile"], 'sub'> }

export type UserDetails = {
  telephone?: string;
  name: string;
  contact_email: string;
  id: string;
  email: string;
  profile: "customer" | "admin" | "employee";
}

function formatTelephoneFieldValue(telephone?: string): string | undefined {
  if (!telephone) return undefined;;
  return telephone.trim();
}

export const getMe = async (sessionToken: string): Promise<UserDetails> => {
  try {
    const splitted = sessionToken.split(".")
    const body = JSON.parse(atob(splitted[1]))
    return {
      name: body.nickname,
      id: body.sub,
      email: body.email,
      contact_email: body.email,
      telephone: formatTelephoneFieldValue(body.address?.formatted),
      profile: body.profile || "customer"
    }
  } catch (error) {
    throw new Error("Token non valido o scaduto");
  }
}

type GetUserResponse = {
  success: true;
  name: string;
  userSub: string;
  support: boolean;
  role: "customer" | "admin" | "employee";
  active: true;
  address: string;
  email: string;
}

export const getUser = async (
  id: string, sessionToken: string
): Promise<UserDetails & { assistence: boolean }> => {
  return cache.getOrProduce(`user:${id}`, async () => {
    const host = configuration.adminApiHost;
    try {
      const response = await fetch(
        `${host}/user/${id}`,
        {
          method: 'GET',
          cache: 'no-cache',
          headers: {
            'Authorization': `Bearer ${sessionToken}`,
            'Accept': 'application/json; indent=4',
          },
          redirect: 'follow',
        }
      ).catch(handleFetchError);
      if(!response.ok)
        throw new Error(response.statusText);
      const responseContent: GetUserResponse = await response.json();
      return {
        name: responseContent.name,
        id: responseContent.userSub,
        email: responseContent.email,
        contact_email: responseContent.email,
        telephone: formatTelephoneFieldValue(responseContent.address),
        profile: responseContent.role || "customer",
        assistence: Boolean(responseContent.support),
      };
    } catch {
      throw new Error("Utente non trovato")
    }
  })
}

type EditableUserFields = Omit<Partial<UserDetails>, 'id'> & {
  password?: string;
}

export const editMe = async (
  me: EditableUserFields, sessionToken: string
): Promise<Partial<UserDetails>> => {
  const attributes: AttributeType[] = []
  if (me.email)
    attributes.push({ Name: 'email', Value: me.email });
  if (attributes.length > 0) {
    const command = new UpdateUserAttributesCommand({
      AccessToken: sessionToken,
      UserAttributes: attributes
    })
    await cognitoIdentityProviderClient.send(command)
  }
  return me
}

export const editMyPhone = async (
  telephone: string, userId: string, sessionToken: string
): Promise<Partial<UserDetails>> => {
  const baseUrl = configuration.adminApiHost;
  const url = `${baseUrl}/user/${userId}`;
  await fetch(
    url,
    {
      method: 'POST',
      cache: 'no-cache',
      headers: {
        'Authorization': `Bearer ${sessionToken}`,
        'Content-Type': 'application/json'
      },
      redirect: 'follow',
      body: JSON.stringify({
        accessToken: sessionToken,
        numbers: telephone,
      }),
    }
  ).catch(handleFetchError);
  return { telephone };
}

export const editMyEmail = async (
  email: string, sessionToken: string
): Promise<Partial<UserDetails>> => {
  return await editMe({ email }, sessionToken);
}

export const sendRecoverEmail = async (email: string): Promise<void> => {
  throw new Error("Not implemented");
}

export const changePassword = async (
  newPassword: string, oldPassword: string, sessionToken: string
): Promise<void> => {
  const changePasswordCommand = new ChangePasswordCommand({
    AccessToken: sessionToken,
    PreviousPassword: oldPassword,
    ProposedPassword: newPassword,
  })

  await cognitoIdentityProviderClient.send(changePasswordCommand)
}

export function phoneNumbersToPhoneString(numbers: string[]) {
  return numbers.join(";").replaceAll(/[^0-9;]+/g, "")
}

export function phoneStringToPhoneNumber(phoneString?: string) {
  return phoneString
    ? phoneString.split(";")
        .map(phoneNumber => `(+${phoneNumber.substring(0,2)}) ${phoneNumber.substring(2, 5)} ${phoneNumber.substring(5)}`)
    : []
}